import { createContext, useContext } from 'react'
import type { AddableKind, EndpointRef, ImpactTarget, IncidentsResponse, LngLat, NetworkResponse, User } from './api'
import type { MapHighlight } from './components/MapView'

export type View =
  | { kind: 'overview' }
  | { kind: 'node'; id: string }
  | { kind: 'cable'; id: string }
  | { kind: 'trace'; serviceId: string }
  | { kind: 'impact'; target: ImpactTarget }
  | { kind: 'otdr'; from?: EndpointRef }
  | { kind: 'quality' }
  | { kind: 'audit' }
  | { kind: 'incidents' }
  | { kind: 'incident'; id: string }
  | { kind: 'add-node' }
  | { kind: 'port-path'; splitterId: string; port: number; nodeId: string }
  | { kind: 'add-cable' }
  | { kind: 'import' }

/** What the map is being used for while adding or moving plant. */
export type EditState =
  | { kind: 'none' }
  | { kind: 'place'; nodeKind: AddableKind; location?: LngLat; picking: boolean }
  | { kind: 'cable'; fromNodeId?: string; via: LngLat[]; toNodeId?: string }
  | { kind: 'move'; nodeId: string; to?: LngLat }

export interface AppCtx {
  user: User
  network: NetworkResponse
  /** Open a view in the side panel; pushes onto the panel's back stack. */
  open: (v: View) => void
  back: () => void
  canGoBack: boolean
  setHighlight: (h: MapHighlight) => void
  focusOn: (lngLat: LngLat, zoom?: number) => void
  /** Fit a polyline (e.g. a cable) in view. */
  focusPath: (path: LngLat[]) => void
  /** Re-fetch the network after a write. */
  refreshNetwork: () => void
  nodeName: (id: string) => string
  /** OSP: add/move/remove plant and splices. */
  canEdit: boolean
  /** NOC, OSP, admin: acknowledge incidents, demo alarms, read the audit log. */
  canAck: boolean
  /** Live incidents, polled. Undefined until the first load. */
  incidents: IncidentsResponse | undefined
  reloadIncidents: () => void
  edit: EditState
  setEdit: (e: EditState) => void
  /** Replace the current panel view (no back-stack entry), e.g. after saving a new asset. */
  replace: (v: View) => void
  /** Short confirmation at the bottom of the screen, optionally with one action (e.g. Undo). */
  notify: (message: string, action?: { label: string; run: () => void | Promise<void> }) => void
}

export const Ctx = createContext<AppCtx | null>(null)

export function useApp(): AppCtx {
  const c = useContext(Ctx)
  if (!c) throw new Error('useApp outside provider')
  return c
}
